import { collection, doc, onSnapshot, orderBy, query, setDoc, where, type DocumentData } from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import type { Product, SiteContent } from "./domain";
import { products as demoProducts } from "./demoData";
import { firebaseConfigured, getFirebaseServices } from "./firebase";
import { adminRoles, type Session } from "./auth";

export const defaultSiteContent: SiteContent = {
  heroEyebrow: "Alabian Kebab by Alabian Food",
  heroTitle: "Frozen kebab premium untuk keluarga dan bisnis.",
  heroDescription: "Belanja sebagai konsumen, dapatkan harga reseller, atau bangun kemitraan B2B melalui satu platform.",
  primaryCta: "Lihat produk",
  secondaryCta: "Program reseller",
  catalogEyebrow: "Katalog frozen kebab",
  catalogTitle: "Pilih rasa favorit.",
  businessEyebrow: "Kembangkan usaha bersama",
  businessTitle: "Harga khusus, pemesanan ulang, dan dokumen bisnis.",
  businessCta: "Daftar sebagai reseller",
  footerNote: "Terima kasih telah mempercayai Alabian Kebab.",
};

function mapProduct(id: string, data: DocumentData): Product {
  const prices = data.prices ?? data.demoPrices ?? {};
  return {
    id,
    name: String(data.name ?? "Produk Alabian"),
    description: String(data.description ?? ""),
    variants: Array.isArray(data.allowedVariants) ? data.allowedVariants.map(String) : Array.isArray(data.variants) ? data.variants.map(String) : [],
    unit: String(data.unit ?? "box"),
    demoPrices: {
      customer: Number(prices.customer ?? 0),
      reseller: Number(prices.reseller ?? prices.customer ?? 0),
      partner: Number(prices.partner ?? prices.reseller ?? prices.customer ?? 0),
    },
    variantPrices: typeof data.variantPrices === "object" && data.variantPrices ? data.variantPrices : undefined,
    featured: Boolean(data.featured),
    active: data.active !== false,
    stock: Number(data.stock ?? 0),
    sortOrder: Number(data.sortOrder ?? 0),
    imageUrl: typeof data.imageUrl === "string" ? data.imageUrl : undefined,
  };
}

export function subscribeCatalog(session: Session | null, onChange: (items: Product[]) => void): () => void {
  const services = getFirebaseServices();
  if (!services) {
    onChange(demoProducts);
    return () => undefined;
  }
  const canManage = Boolean(session && adminRoles.includes(session.role));
  const productQuery = canManage
    ? query(collection(services.db, "products"), orderBy("sortOrder", "asc"))
    : query(collection(services.db, "products"), where("active", "==", true), orderBy("sortOrder", "asc"));
  return onSnapshot(productQuery, (snapshot) => {
    const items = snapshot.docs.map((item) => mapProduct(item.id, item.data()));
    onChange(items.length > 0 ? items : demoProducts);
  }, () => onChange(demoProducts));
}

export function subscribeSiteContent(onChange: (content: SiteContent) => void): () => void {
  const services = getFirebaseServices();
  if (!services) {
    onChange(defaultSiteContent);
    return () => undefined;
  }
  return onSnapshot(doc(services.db, "appSettings", "site"), (snapshot) => {
    onChange({ ...defaultSiteContent, ...(snapshot.exists() ? snapshot.data() : {}) });
  }, () => onChange(defaultSiteContent));
}

export async function saveProduct(product: Product): Promise<void> {
  const services = getFirebaseServices();
  if (!services || !firebaseConfigured) throw new Error("Firebase development belum tersambung.");
  await setDoc(doc(services.db, "products", product.id), {
    name: product.name.trim(),
    description: product.description.trim(),
    unit: product.unit.trim() || "box",
    allowedVariants: product.variants.map((item) => item.trim()).filter(Boolean),
    prices: product.demoPrices,
    variantPrices: product.variantPrices ?? {},
    featured: Boolean(product.featured),
    active: product.active !== false,
    stock: Math.max(0, Number(product.stock ?? 0)),
    sortOrder: Number(product.sortOrder ?? 0),
    imageUrl: product.imageUrl ?? "",
    updatedAt: new Date(),
  }, { merge: true });
}

export async function uploadProductImage(productId: string, file: File): Promise<string> {
  const services = getFirebaseServices();
  if (!services) throw new Error("Firebase Storage belum tersambung.");
  if (!file.type.startsWith("image/")) throw new Error("File foto harus berupa gambar.");
  if (file.size > 8 * 1024 * 1024) throw new Error("Ukuran foto maksimal 8 MB.");
  const extension = file.name.split(".").pop()?.toLowerCase().replace(/[^a-z0-9]/g, "") || "jpg";
  const target = ref(services.storage, `products/${productId}/original-${Date.now()}.${extension}`);
  await uploadBytes(target, file, { contentType: file.type, customMetadata: { preservation: "original-upload" } });
  return getDownloadURL(target);
}

export async function saveSiteContent(content: SiteContent): Promise<void> {
  const services = getFirebaseServices();
  if (!services) throw new Error("Firebase development belum tersambung.");
  await setDoc(doc(services.db, "appSettings", "site"), { ...content, public: true, updatedAt: new Date() }, { merge: true });
}

export async function seedInitialCatalog(): Promise<void> {
  for (const [index, product] of demoProducts.entries()) {
    await saveProduct({ ...product, active: true, stock: 0, sortOrder: index + 1 });
  }
  await saveSiteContent(defaultSiteContent);
}
